import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { ThemeContext } from "../../context/Theme/ThemeContext";
import MainContainer from "../../components/MainContainer";
import AppPageTitle from "../../components/AppPageTitle";
import AppTableActionMenu from "../../components/AppTableActionMenu";
import AppModal from "../../components/AppModal";
import TemplateModal from "../../components/TemplateModal";
import TemplateConversationPreview from "../../components/TemplateConversationPreview";
import ConfirmationModal from "../../components/ConfirmationModal";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import apiV2 from "../../services/apiV2";
import toastError from "../../errors/toastError";

const PAGE_SIZE = 10;

function IconSearch() {
	return (
		<svg className="size-4 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

function IconPlus() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M12 5v14M5 12h14" />
		</svg>
	);
}

function IconMore() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="1" />
			<circle cx="19" cy="12" r="1" />
			<circle cx="5" cy="12" r="1" />
		</svg>
	);
}

function IconTrash() {
	return (
		<svg className="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		</svg>
	);
}

function IconChevronLeft({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="15 18 9 12 15 6" />
		</svg>
	);
}

function IconChevronRight({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="9 18 15 12 9 6" />
		</svg>
	);
}

function IconSpark() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="m12 3 1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3Z" />
		</svg>
	);
}

function IconClock() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="10" />
			<polyline points="12 6 12 12 16 14" />
		</svg>
	);
}

function IconLayers() {
	return (
		<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="m12 3-9 4.5 9 4.5 9-4.5L12 3Z" />
			<path d="m3 12 9 4.5 9-4.5" />
			<path d="m3 16.5 9 4.5 9-4.5" />
		</svg>
	);
}

function IconEye() {
	return (
		<svg className="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
			<circle cx="12" cy="12" r="3" />
		</svg>
	);
}

function SummaryCard({ title, value, hint, icon, toneClassName }) {
	return (
		<div className="card h-full">
			<div className="card-body flex items-start justify-between gap-4">
				<div>
					<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
						{title}
					</p>
					<p className="m-0 text-2xl font-semibold text-default-800 dark:text-default-100">
						{value}
					</p>
					<p className="mt-2 mb-0 text-sm text-default-500 dark:text-default-400">
						{hint}
					</p>
				</div>
				<div className={`flex size-12 shrink-0 items-center justify-center rounded-2xl ${toneClassName}`}>
					{icon}
				</div>
			</div>
		</div>
	);
}

function WarningGuide() {
	return (
		<div className="mb-5 rounded-2xl border border-warning/20 bg-warning/10 px-5 py-4 text-warning-900 dark:border-warning/30 dark:bg-warning/15 dark:text-warning-100">
			<p className="mb-2 text-sm font-semibold">Antes de criar templates</p>
			<div className="space-y-1 text-sm leading-6">
				<p className="m-0">1. A conexão precisa ter `WABA Account Id` e `WABA Access Token` configurados.</p>
				<p className="m-0">2. Templates de imagem ou vídeo dependem de um template modelo já aprovado na Meta.</p>
				<p className="m-0">3. Para templates de texto, revise cabeçalho, corpo e rodapé antes de enviar para aprovação.</p>
				<p className="m-0">4. Modelos customizados servem para reaproveitar mídia em fluxos e envios internos.</p>
			</div>
		</div>
	);
}

function getTemplateComponent(template, type, fallbackIndex) {
	return (
		template?.components?.find(component => component.type === type) ||
		template?.components?.[fallbackIndex] ||
		null
	);
}

function getTemplateHeaderLabel(template) {
	const headerComponent = getTemplateComponent(template, "HEADER", 0);

	if (!headerComponent) return "—";
	if (headerComponent.format === "TEXT" || !headerComponent.format) {
		return headerComponent.text || "—";
	}

	const formatMap = {
		IMAGE: "Imagem",
		VIDEO: "Vídeo",
		DOCUMENT: "Documento",
	};

	return formatMap[headerComponent.format] || headerComponent.format;
}

function getTemplateBodyLabel(template) {
	return getTemplateComponent(template, "BODY", 1)?.text || "—";
}

function getTemplateFooterLabel(template) {
	return getTemplateComponent(template, "FOOTER", 2)?.text || "—";
}

function getStatusMeta(status, source) {
	if (source === "custom") {
		return {
			label: "Customizado",
			className: "bg-info/10 text-info",
		};
	}

	const map = {
		APPROVED: {
			label: i18n.t("templates.table.statuses.APPROVED"),
			className: "bg-success/10 text-success",
		},
		PENDING: {
			label: i18n.t("templates.table.statuses.PENDING"),
			className: "bg-warning/10 text-warning",
		},
		REJECTED: {
			label: i18n.t("templates.table.statuses.REJECTED"),
			className: "bg-danger/10 text-danger",
		},
	};

	return map[status] || { label: status || "—", className: "bg-default-100 text-default-700" };
}

function SourceBadge({ source }) {
	const map = {
		meta: "bg-primary/10 text-primary",
		custom: "bg-secondary/10 text-secondary",
	};

	return (
		<span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${map[source] || "bg-default-100 text-default-700"}`}>
			{source === "custom" ? "Modelo" : "Meta"}
		</span>
	);
}

function StatusBadge({ status, source }) {
	const meta = getStatusMeta(status, source);
	return (
		<span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${meta.className}`}>
			{meta.label}
		</span>
	);
}

function normalizeSearch(value) {
	return String(value || "").toLowerCase().trim();
}

export default function WpTemplates() {
	const { whatsApps } = useContext(WhatsAppsContext);
	const { darkMode } = useContext(ThemeContext);
	const [metaTemplates, setMetaTemplates] = useState([]);
	const [customTemplates, setCustomTemplates] = useState([]);
	const [templateModalOpen, setTemplateModalOpen] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState(null);
	const [previewTemplate, setPreviewTemplate] = useState(null);
	const [loading, setLoading] = useState(false);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [confirmationFunction, setConfirmationFunction] = useState(null);
	const [searchParam, setSearchParam] = useState("");
	const [sourceFilter, setSourceFilter] = useState("all");
	const [connectionFilter, setConnectionFilter] = useState("all");
	const [pageNumber, setPageNumber] = useState(1);
	const [refreshKey, setRefreshKey] = useState(0);

	useEffect(() => {
		if (!confirmationOpen) {
			setConfirmationFunction(null);
		}
	}, [confirmationOpen]);

	const loadTemplates = useCallback(async () => {
		setLoading(true);

		try {
			const hubConnections = (whatsApps || []).filter(whatsApp => {
				const channel = String(whatsApp?.channel || "").toLowerCase();
				return (
					whatsApp?.id &&
					channel === "whatsapp_business_account" &&
					(whatsApp.hub === true || whatsApp.hub === 1)
				);
			});

			const [customResponse, hubResponses] = await Promise.all([
				api.get("/template/"),
				Promise.allSettled(
					hubConnections.map(async whatsApp => {
						const { data } = await api.get(
							`/hub-tamplate?whatsappId=${whatsApp.id}`
						);
						const list = Array.isArray(data?.data)
							? data.data
							: Array.isArray(data)
								? data
								: [];

						return list.map(template => ({ ...template, whatsApp }));
					})
				),
			]);

			const nextMetaTemplates = hubResponses.flatMap(result =>
				result.status === "fulfilled" ? result.value : []
			);

			hubResponses.forEach(result => {
				if (result.status === "rejected") {
					console.error("Falha ao carregar templates Hub:", result.reason);
				}
			});

			setCustomTemplates(Array.isArray(customResponse.data) ? customResponse.data : []);
			setMetaTemplates(nextMetaTemplates);
		} catch (err) {
			toastError(err);
			setCustomTemplates([]);
			setMetaTemplates([]);
		} finally {
			setLoading(false);
		}
	}, [whatsApps]);

	useEffect(() => {
		loadTemplates();
	}, [loadTemplates, refreshKey]);

	useEffect(() => {
		setPageNumber(1);
	}, [searchParam, sourceFilter, connectionFilter]);

	const rows = useMemo(() => {
		const whatsAppMap = new Map(whatsApps.map(item => [String(item.id), item]));

		const customRows = customTemplates.map(template => {
			const connection = whatsAppMap.get(String(template.connectionId));
			return {
				id: `custom-${template.id}`,
				source: "custom",
				raw: template,
				name: template.name || "—",
				connectionName: connection?.name || "—",
				category: template.type ? String(template.type).toUpperCase() : "—",
				status: "CUSTOM",
				header: template.name || "—",
				body: template.templateName ? `Modelo: ${template.templateName}` : "—",
				footer: template.mediaPath || "—",
				searchContent: [
					template.name,
					template.templateName,
					template.type,
					connection?.name,
					template.mediaPath,
				]
					.filter(Boolean)
					.join(" "),
			};
		});

		const metaRows = metaTemplates.map(template => ({
			id: `meta-${template.id}`,
			source: "meta",
			raw: template,
			name: template.name || "—",
			connectionName: template.whatsApp?.name || "—",
			category: template.category || "—",
			status: template.status,
			header: getTemplateHeaderLabel(template),
			body: getTemplateBodyLabel(template),
			footer: getTemplateFooterLabel(template),
			searchContent: [
				template.name,
				template.category,
				template.status,
				template.whatsApp?.name,
				getTemplateHeaderLabel(template),
				getTemplateBodyLabel(template),
				getTemplateFooterLabel(template),
			]
				.filter(Boolean)
				.join(" "),
		}));

		return [...metaRows, ...customRows];
	}, [customTemplates, metaTemplates, whatsApps]);

	const filteredRows = useMemo(() => {
		const term = normalizeSearch(searchParam);

		return rows.filter(row => {
			const matchesSearch = !term || normalizeSearch(row.searchContent).includes(term);
			const matchesSource = sourceFilter === "all" || row.source === sourceFilter;
			const matchesConnection =
				connectionFilter === "all" || normalizeSearch(row.connectionName) === normalizeSearch(connectionFilter);

			return matchesSearch && matchesSource && matchesConnection;
		});
	}, [rows, searchParam, sourceFilter, connectionFilter]);

	const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));

	const paginatedRows = useMemo(() => {
		const start = (pageNumber - 1) * PAGE_SIZE;
		return filteredRows.slice(start, start + PAGE_SIZE);
	}, [filteredRows, pageNumber]);

	const pageNumbers = () => {
		if (totalPages <= 1) return [1];
		let from = Math.max(1, pageNumber - 2);
		let to = Math.min(totalPages, from + 4);
		if (to - from < 4) {
			from = Math.max(1, to - 4);
		}
		return Array.from({ length: to - from + 1 }, (_, index) => from + index);
	};

	const summary = useMemo(
		() => ({
			total: rows.length,
			approved: metaTemplates.filter(
				template => String(template?.status || "").toUpperCase() === "APPROVED"
			).length,
			pending: metaTemplates.filter(
				template => String(template?.status || "").toUpperCase() === "PENDING"
			).length,
			custom: customTemplates.length,
		}),
		[customTemplates.length, metaTemplates, rows.length]
	);

	const rangeStart = filteredRows.length ? (pageNumber - 1) * PAGE_SIZE + 1 : 0;
	const rangeEnd = Math.min(pageNumber * PAGE_SIZE, filteredRows.length);

	const connectionOptions = useMemo(
		() =>
			Array.from(
				new Set(rows.map(row => row.connectionName).filter(name => name && name !== "—"))
			).sort((first, second) => first.localeCompare(second)),
		[rows]
	);

	const handleCloseModal = () => {
		setSelectedTemplate(null);
		setTemplateModalOpen(false);
		setRefreshKey(current => current + 1);
	};

	const openTemplatePreview = template => {
		if (!template) return;
		setPreviewTemplate(template);
	};

	const handleDeleteTemplate = async template => {
		try {
			const whatsappId = template?.whatsApp?.id;
			const name = template?.name;
			if (!whatsappId || !name) {
				throw new Error("Template sem conexão ou nome");
			}

			await apiV2.delete(
				`/hub/templates/${whatsappId}/${encodeURIComponent(name)}`
			);

			setMetaTemplates(current => current.filter(item => item.id !== template.id));
			toast.success(i18n.t("templates.toasts.deleted"));
		} catch (err) {
			toastError(err);
		} finally {
			setConfirmationOpen(false);
		}
	};

	return (
		<MainContainer>
			<div className="app-admin-page">
				<ConfirmationModal
					title="Excluir template"
					open={confirmationOpen}
					onClose={setConfirmationOpen}
					onConfirm={confirmationFunction}
				>
					{i18n.t("messageOptionsMenu.confirmationModal.message")}
				</ConfirmationModal>

				{templateModalOpen && (
					<TemplateModal
						open={templateModalOpen}
						onClose={handleCloseModal}
						whatsApps={whatsApps}
						selectedTemplate={
							selectedTemplate
								? {
										...selectedTemplate,
										header: selectedTemplate?.components?.[0]?.text,
										body: selectedTemplate?.components?.[1]?.text,
										footer: selectedTemplate?.components?.[2]?.text,
									}
								: {}
						}
					/>
				)}

				<AppModal
					open={Boolean(previewTemplate)}
					onClose={() => setPreviewTemplate(null)}
					title="Preview do template"
					subtitle={
						previewTemplate
							? `${previewTemplate.name?.replace(/_/g, " ") || ""} · ${
									previewTemplate.whatsApp?.name || "Meta"
								}`
							: ""
					}
					maxWidth={420}
					footer={
						<button
							type="button"
							className="btn btn-sm border border-default-200 bg-transparent"
							onClick={() => setPreviewTemplate(null)}
						>
							Fechar
						</button>
					}
				>
					<TemplateConversationPreview
						template={previewTemplate}
						params={[]}
						darkMode={Boolean(darkMode)}
					/>
				</AppModal>

				<div className="app-admin-page__scroll">
					<AppPageTitle title={i18n.t("templates.title")} subtitle="Administração" />

					<div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
						<SummaryCard
							title="Total"
							value={summary.total}
							hint="Templates Hub/Meta e modelos customizados."
							icon={<IconLayers />}
							toneClassName="bg-primary/10 text-primary"
						/>
						<SummaryCard
							title="Aprovados"
							value={summary.approved}
							hint="Templates prontos para uso pela Meta."
							icon={<IconSpark />}
							toneClassName="bg-success/10 text-success"
						/>
						<SummaryCard
							title="Pendentes"
							value={summary.pending}
							hint="Templates aguardando análise da Meta."
							icon={<IconClock />}
							toneClassName="bg-warning/10 text-warning"
						/>
						<SummaryCard
							title="Customizados"
							value={summary.custom}
							hint="Modelos internos vinculados a mídia."
							icon={<IconLayers />}
							toneClassName="bg-info/10 text-info"
						/>
					</div>
{/* 
					<WarningGuide /> */}

					<div className="card">
						<div className="card-header flex-wrap items-start justify-between gap-3 px-5 pt-5 pb-4">
							<div>
								<h6 className="card-title m-0">{i18n.t("templates.title")}</h6>
								<p className="mt-1 mb-0 text-sm text-default-500 dark:text-default-400">
									Templates Hub (aprovados, pendentes e rejeitados). Clique para ver o preview.
								</p>
							</div>
							<button
								type="button"
								className="btn btn-sm shrink-0 bg-primary text-white"
								onClick={() => setTemplateModalOpen(true)}
							>
								<IconPlus />
								{i18n.t("templates.buttons.add")}
							</button>
						</div>

						<div className="card-header grid grid-cols-1 gap-3 px-5 py-4 lg:grid-cols-[minmax(0,1fr)_220px_220px]">
							<div className="relative">
								<input
									type="search"
									className="form-input form-input-sm w-full ps-11"
									placeholder="Pesquisar templates..."
									value={searchParam}
									onChange={event => setSearchParam(event.target.value)}
								/>
								<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
									<IconSearch />
								</div>
							</div>

							<select
								className="form-select form-select-sm"
								value={sourceFilter}
								onChange={event => setSourceFilter(event.target.value)}
							>
								<option value="all">Todas as origens</option>
								<option value="meta">Meta / Hub</option>
								<option value="custom">Modelos customizados</option>
							</select>

							<select
								className="form-select form-select-sm"
								value={connectionFilter}
								onChange={event => setConnectionFilter(event.target.value)}
							>
								<option value="all">Todas as conexões</option>
								{connectionOptions.map(option => (
									<option key={option} value={option}>
										{option}
									</option>
								))}
							</select>
						</div>

						<div className="flex min-h-0 flex-1 flex-col">
							{loading ? (
								<div className="flex items-center justify-center px-6 py-16">
									<div className="inline-flex items-center gap-2 text-sm text-default-500">
										<div className="app-modal-spinner !size-4" />
										<span>Carregando templates...</span>
									</div>
								</div>
							) : filteredRows.length === 0 ? (
								<div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
									<p className="m-0 font-semibold text-default-800 dark:text-default-100">
										Nenhum template encontrado
									</p>
									<p className="m-0 text-sm text-default-500 dark:text-default-400">
										Revise os filtros, as credenciais WABA da conexão ou crie um novo template.
									</p>
								</div>
							) : (
								<div className="flex min-h-0 flex-1 flex-col">
									<div className="app-table-scroll">
										<div className="inline-block min-w-full align-middle">
											<div className="overflow-visible">
												<table className="min-w-full divide-y divide-default-200 dark:divide-default-700">
													<thead className="bg-default-150 dark:bg-default-800/60">
														<tr className="text-sm font-normal text-default-700 dark:text-default-300">
															<th className="px-3.5 py-3 text-start">Origem</th>
															<th className="px-3.5 py-3 text-start">Nome</th>
															<th className="px-3.5 py-3 text-start">Conexão</th>
															<th className="px-3.5 py-3 text-start">
																{i18n.t("templates.table.type")}
															</th>
															<th className="px-3.5 py-3 text-start">
																{i18n.t("templates.table.status")}
															</th>
															<th className="px-3.5 py-3 text-start">
																{i18n.t("templates.table.header")}
															</th>
															<th className="px-3.5 py-3 text-start">
																{i18n.t("templates.table.body")}
															</th>
															<th className="px-3.5 py-3 text-start">
																{i18n.t("templates.table.footer")}
															</th>
															<th className="px-3.5 py-3 text-center">
																{i18n.t("templates.table.actions")}
															</th>
														</tr>
													</thead>
													<tbody className="divide-y divide-default-200 dark:divide-default-700">
														{paginatedRows.map(row => {
															const isMeta = row.source === "meta";

															return (
																<tr
																	key={row.id}
																	className={`text-sm text-default-800 dark:text-default-200 ${
																		isMeta
																			? "cursor-pointer transition-colors hover:bg-primary/5"
																			: ""
																	}`}
																	onClick={() => {
																		if (isMeta) openTemplatePreview(row.raw);
																	}}
																>
																	<td className="px-3.5 py-3">
																		<SourceBadge source={row.source} />
																	</td>
																	<td className="px-3.5 py-3">
																		<p className="m-0 max-w-[14rem] truncate font-medium">
																			{row.name?.replace(/_/g, " ")}
																		</p>
																	</td>
																	<td className="px-3.5 py-3">
																		<div className="max-w-[12rem] truncate">{row.connectionName}</div>
																	</td>
																	<td className="px-3.5 py-3">
																		<div className="max-w-[10rem] truncate">
																			{row.source === "meta" && row.category !== "—"
																				? i18n.t(`templates.table.types.${row.category}`, {
																						defaultValue: row.category,
																					})
																				: row.category}
																		</div>
																	</td>
																	<td className="px-3.5 py-3">
																		<StatusBadge status={row.status} source={row.source} />
																	</td>
																	<td className="px-3.5 py-3">
																		<p className="m-0 max-w-[16rem] truncate">{row.header}</p>
																	</td>
																	<td className="px-3.5 py-3">
																		<p className="m-0 max-w-[18rem] truncate">{row.body}</p>
																	</td>
																	<td className="px-3.5 py-3">
																		<p className="m-0 max-w-[16rem] truncate">{row.footer}</p>
																	</td>
																	<td
																		className="px-3.5 py-3 text-center"
																		onClick={event => event.stopPropagation()}
																	>
																		{row.source === "meta" ? (
																			<div className="inline-flex justify-center">
																				<AppTableActionMenu
																					triggerIcon={<IconMore />}
																					triggerAriaLabel={i18n.t("templates.table.actions")}
																					minWidth={160}
																					items={[
																						{
																							key: "preview",
																							label: "Ver preview",
																							icon: <IconEye />,
																							onClick: () => openTemplatePreview(row.raw),
																						},
																						{
																							key: "delete",
																							label: "Excluir",
																							icon: <IconTrash />,
																							tone: "danger",
																							onClick: () => {
																								setConfirmationFunction(
																									() => () => handleDeleteTemplate(row.raw)
																								);
																								setConfirmationOpen(true);
																							},
																						},
																					]}
																				/>
																			</div>
																		) : (
																			<span className="text-xs text-default-500">—</span>
																		)}
																	</td>
																</tr>
															);
														})}
													</tbody>
												</table>
											</div>
										</div>
									</div>

									<div className="card-footer">
										<p className="m-0 text-sm text-default-500">
											Exibindo {rangeStart} a {rangeEnd} de {filteredRows.length} template(s)
										</p>

										{totalPages > 1 && (
											<nav className="flex flex-wrap items-center gap-2" aria-label="Paginação de templates">
												<button
													type="button"
													className="btn btn-sm shrink-0 border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
													disabled={pageNumber <= 1}
													onClick={() => setPageNumber(current => Math.max(1, current - 1))}
												>
													<IconChevronLeft className="size-4 me-1 shrink-0" />
													Anterior
												</button>
												{pageNumbers().map(page => (
													<button
														key={page}
														type="button"
														className={`btn size-7.5 shrink-0 border ${
															page === pageNumber
																? "border-primary bg-primary text-white"
																: "border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
														}`}
														onClick={() => setPageNumber(page)}
													>
														{page}
													</button>
												))}
												<button
													type="button"
													className="btn btn-sm shrink-0 border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
													disabled={pageNumber >= totalPages}
													onClick={() => setPageNumber(current => Math.min(totalPages, current + 1))}
												>
													Próxima
													<IconChevronRight className="size-4 ms-1 shrink-0" />
												</button>
											</nav>
										)}
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</MainContainer>
	);
}
